import AbstractGlobe from '@assets/abstract-globe.png';
import { PaginationReturn } from '@common/types/service';
import { Roles } from '@common/types/user';
import { PlusAccordionSummary } from '@components/display/accordions';
import { Flag } from '@components/display/images';
import { Table } from '@components/display/tables';
import { Filters } from '@components/utility/filters';
import { DateRangePicker } from '@components/utility/pickers';
import { Option, Select } from '@components/utility/selects';
import { Tabs } from '@components/utility/tabs';
import { EventCreationDialog } from '@event/components/display/dialogs';
import {
    DATE_PICKER_SHORTCUTS,
    PAST_ALERT_MESSAGE,
    PAST_DATE_PICKER_SHORTCUTS,
    UPCOMING_ALERT_MESSAGE,
    UPCOMING_DATE_PICKER_SHORTCUTS,
} from '@event/constants';
import {
    useAllEventsQuery,
    useGetBadgesAndLevelsOptionsQuery,
    useGetEventOrganizersQuery,
    useGetLanguageSpokenOptionsQuery,
} from '@event/services/events';
import { EventTypes, IEvent, ManagementEventListItem } from '@event/types/event';
import {
    ATTENDEE_PRESENT,
    ATTENDEE_STATUS,
    EVENT_DATE_FILTERS,
    EVENT_ORGANIZER,
    EVENT_STATUS,
    EVENT_TABLE_HEADERS,
    EVENT_TIMEFRAME,
    EVENT_TYPES,
    getEventTypeName,
    PRIVACY_FILTER_OPTIONS,
} from '@event/utils/event';
import { useAppDispatch } from '@hooks/generic';
import { useAuth } from '@hooks/useAuth';
import { useDebounce } from '@hooks/useDebounce';
import { BadgeDisplay } from '@levelSystem/components/badges';
import AddIcon from '@mui/icons-material/Add';
import {
    Accordion,
    AccordionDetails,
    Button,
    Chip,
    ListItemText,
    Tabs as MUITabs,
    SelectChangeEvent,
    Tab,
    TableCell,
    Typography,
} from '@mui/material';
import { useGetMyEventsQuery } from '@services/accounts';
import { useGetCountriesQuery } from '@services/countries';
import { setTitle } from '@store/appSlice';
import { capitalizeFirstLetter, isDateStringValid, isDateValid, YES_NO } from '@utils/common';
import { validateRange } from '@utils/date';
import { startOfToday } from 'date-fns';
import { format } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const ROWS_PER_PAGE_OPTIONS = [
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

const allowedColumns: (keyof ManagementEventListItem)[] = [
    'published',
    'type',
    'name',
    'date_start',
    'country_name',
    'max_capacity',
];

enum Timeframes {
    UPCOMING = 'upcoming',
    PAST = 'past',
    ALL = 'all',
}

const allowedTimeframes = Object.values(Timeframes);

enum MyTableFilters {
    PRIVACY = 'my_privacy',
    DATE_RANGE_START = 'my_date_range_start',
    DATE_RANGE_END = 'my_date_range_end',
    OWNER = 'my_owner_id',
    STATUS = 'my_published',
    COUNTRY = 'my_country_code',
    LANGUAGE = 'my_spoken_language',
    BADGE_LEVEL = 'my_level_id',
    BADGE_SOLUTION = 'my_badge_id',
}

enum AllTableFilters {
    PRIVACY = 'privacy',
    DATE_RANGE_START = 'date_range_start',
    DATE_RANGE_END = 'date_range_end',
    OWNER = 'owner_id',
    STATUS = 'published',
    COUNTRY = 'country_code',
    LANGUAGE = 'spoken_language',
    BADGE_LEVEL = 'level_id',
    BADGE_SOLUTION = 'badge_id',
}

enum MY_TABS {
    ORGANIZED = 0,
    COHOST = 1,
}

const getDateRangeParamValue = (date?: Date) => (date && isDateValid(date) ? format(date, 'yyyy-MM-dd') : '');

const getValidDateFromParam = (value?: string) => (value && isDateStringValid(value) ? new Date(value) : null);

const EventList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [dateError, setDateError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState({
        my: true,
        all: true,
    });

    const { data: languageOptions, isLoading: areLanguageOptionsLoading } = useGetLanguageSpokenOptionsQuery(
        undefined,
        {
            selectFromResult: (response) => ({
                ...response,
                data: [
                    {
                        label: 'None',
                        value: '',
                    },
                    ...(response.data?.map((language) => ({ label: language.name, value: language.code })) ?? []),
                ],
            }),
        }
    );

    const myTab = useMemo(() => {
        const myTabParam = searchParams.get('myTab') ?? 0;
        return myTabParam in MY_TABS ? (Number(myTabParam) as MY_TABS) : MY_TABS.ORGANIZED;
    }, [searchParams]);

    const getTableFilterValues = useCallback(
        (myTableFilters?: boolean): Record<string, string[]> =>
            Object.fromEntries(
                Object.values(myTableFilters ? MyTableFilters : AllTableFilters)
                    .map((filter) => {
                        const value = searchParams.get(filter);
                        return value ? [filter, value.includes(',') ? value.split(',') : [value]] : [filter, ''];
                    })
                    .filter((x) => x[1] !== '')
            ),
        [searchParams]
    );

    // Unified table filters for both My Events and All Events
    const myTableFilters = useMemo(() => getTableFilterValues(true), [getTableFilterValues]);
    const allTableFilters = useMemo(() => getTableFilterValues(false), [getTableFilterValues]);

    const getEndpointFilters = useCallback(
        (myTableFilters: boolean): Record<string, string[]> => {
            const filterValues = getTableFilterValues(myTableFilters);
            return {
                ...Object.fromEntries(
                    Object.entries(filterValues)
                        .map(([key, value]) => [key.replace('my_', ''), value])
                        .filter(([, value]) => value && value.length > 0)
                ),
                country_code: (() => {
                    const value = filterValues[`${myTableFilters ? 'my_' : ''}country_code`];
                    return value?.filter((code) => code !== 'webinar') ?? [];
                })(),
                type: (() => {
                    const value = filterValues[`${myTableFilters ? 'my_' : ''}privacy`];
                    const countryCode = searchParams.get(
                        myTableFilters ? MyTableFilters.COUNTRY : AllTableFilters.COUNTRY
                    );
                    let newTypeFilters: string[] = [];
                    if (!value || value.length === 0) {
                        if (countryCode && countryCode.includes('webinar'))
                            return [
                                EventTypes.PUBLIC_VIRTUAL.replace('-', '_'),
                                EventTypes.PRIVATE_VIRTUAL.replace('-', '_'),
                            ];
                        else return [];
                    }
                    if (value.includes('private')) {
                        newTypeFilters.push(
                            EventTypes.PRIVATE_ONSITE.replace('-', '_'),
                            EventTypes.PRIVATE_VIRTUAL.replace('-', '_')
                        );
                    }
                    if (value.includes('public')) {
                        newTypeFilters.push(
                            EventTypes.PUBLIC_ONSITE.replace('-', '_'),
                            EventTypes.PUBLIC_VIRTUAL.replace('-', '_')
                        );
                    }
                    if (countryCode && countryCode.includes('webinar')) {
                        newTypeFilters = newTypeFilters.filter((type) =>
                            [
                                EventTypes.PRIVATE_VIRTUAL.replace('-', '_'),
                                EventTypes.PUBLIC_VIRTUAL.replace('-', '_'),
                            ].includes(type)
                        );
                    }
                    return newTypeFilters;
                })(),
            };
        },
        [searchParams, getTableFilterValues]
    );

    // Computed endpoint filters for both sections
    const myEndpointFilters = useMemo(() => getEndpointFilters(true), [getEndpointFilters]);
    const allEndpointFilters = useMemo(() => getEndpointFilters(false), [getEndpointFilters]);

    const timeframe = useMemo(
        () =>
            allowedTimeframes.includes((searchParams.get('timeframe') ?? '') as Timeframes)
                ? ((searchParams.get('timeframe')?.toString() ?? Timeframes.UPCOMING) as Timeframes)
                : Timeframes.UPCOMING,
        [searchParams]
    );

    const filters = useMemo(
        () => ({
            sort: allowedColumns.includes(searchParams.get('sort')?.replace('-', '') as keyof ManagementEventListItem)
                ? (searchParams.get('sort') as keyof ManagementEventListItem)
                : timeframe === Timeframes.UPCOMING
                ? 'date_start'
                : '-date_start',
            mySort: allowedColumns.includes(
                searchParams.get('mySort')?.replace('-', '') as keyof ManagementEventListItem
            )
                ? (searchParams.get('mySort') as keyof ManagementEventListItem)
                : timeframe === Timeframes.UPCOMING
                ? 'date_start'
                : '-date_start',
            page:
                Number.isNaN(searchParams.get('page')) || !searchParams.get('page')
                    ? '1'
                    : searchParams.get('page') ?? '1',
            myPage:
                Number.isNaN(searchParams.get('myPage')) || !searchParams.get('myPage')
                    ? '1'
                    : searchParams.get('myPage') ?? '1',
            search: searchParams.get('search') ?? '',
            mySearch: searchParams.get('mySearch') ?? '',
            rows:
                Number.isNaN(searchParams.get('rows')) || !searchParams.get('rows')
                    ? '10'
                    : searchParams.get('rows') ?? '10',
            myRows:
                Number.isNaN(searchParams.get('myRows')) || !searchParams.get('myRows')
                    ? '10'
                    : searchParams.get('myRows') ?? '10',
        }),
        [searchParams, timeframe]
    );
    const [search, setSearch] = useState(filters.search as string);
    const [mySearch, setMySearch] = useState(filters.mySearch as string);
    const sort = useMemo(
        () => ({
            column: ((filters.sort as string) || undefined)?.replace('-', '') as keyof ManagementEventListItem,
            direction: ((filters.sort as string) || undefined)?.includes('-') ? 'desc' : ('asc' as 'desc' | 'asc'),
        }),
        [filters.sort]
    );
    const mySort = useMemo(
        () => ({
            column: ((filters.mySort as string) || undefined)?.replace('-', '') as keyof ManagementEventListItem,
            direction: ((filters.mySort as string) || undefined)?.includes('-') ? 'desc' : ('asc' as 'desc' | 'asc'),
        }),
        [filters.mySort]
    );

    const { user, checkUserPerms } = useAuth();
    const isDistributorOrAmbassador = useMemo(
        () => checkUserPerms([Roles.DISTRIBUTOR, Roles.AMBASSADOR]),
        [checkUserPerms]
    );

    // To count, for the tab number
    const { data: allMyUpcomingEventsCount } = useGetMyEventsQuery({
        page_size: 10,
        page: 1,
        filters: { timeframe: [Timeframes.UPCOMING] },
    });
    const { data: allUpcomingEventsCount } = useAllEventsQuery(
        {
            page_size: 10,
            page: 1,
            filters: { timeframe: [Timeframes.UPCOMING] },
        },
        {
            skip: !user || isDistributorOrAmbassador,
        }
    );

    // For counting the Organized and Co-host tabs inside "My" section
    const { data: allMyOrganizedEventsData, isFetching: isMyOrganizedEventsFetching } = useGetMyEventsQuery(
        {
            page_size: 10,
            page: 1,
            filters: {
                timeframe: timeframe !== Timeframes.ALL ? [timeframe] : [],
                owner_id: user ? [user.id.toString()] : [],
            },
        },
        {
            skip: !user,
        }
    );
    const { data: allMyCohostEventsData, isFetching: isMyCohostEventsFetching } = useGetMyEventsQuery(
        {
            page_size: 10,
            page: 1,
            filters: {
                timeframe: timeframe !== Timeframes.ALL ? [timeframe] : [],
                organizer_id: user ? [user.id.toString()] : [],
            },
        },
        {
            skip: !user,
        }
    );

    // For the first table (My)
    const { data: allMyEventsData, isFetching: isMyEventsFetching } = useGetMyEventsQuery(
        {
            page_size: Number(filters.myRows),
            filters: {
                ...myEndpointFilters,
                search: [filters.mySearch],
                timeframe: timeframe !== Timeframes.ALL ? [timeframe] : [],
                owner_id: myTab === MY_TABS.ORGANIZED && user ? [user.id.toString()] : [],
                organizer_id: myTab === MY_TABS.COHOST && user ? [user.id.toString()] : [],
            },
            page: Number(filters.myPage) || 1,
            ordering: filters.mySort,
        },
        {
            skip: !user,
        }
    );

    // For the second table (All)
    const { data: allEventsData, isFetching: isAllEventsFetching } = useAllEventsQuery(
        {
            page_size: Number(filters.rows),
            filters: {
                ...allEndpointFilters,
                search: [filters.search],
                timeframe: timeframe !== Timeframes.ALL ? [timeframe] : [],
            },
            page: Number(filters.page) || 1,
            ordering: filters.sort,
        },
        {
            skip: !user || isDistributorOrAmbassador,
        }
    );

    const { upcomingCount, allMyEvents, allEvents, myOrganizedEventsData, myCohostEventsData } = useMemo(
        () => ({
            upcomingCount: (allUpcomingEventsCount ? allUpcomingEventsCount : allMyUpcomingEventsCount) as
                | PaginationReturn<ManagementEventListItem>
                | undefined,
            myOrganizedEventsData: allMyOrganizedEventsData as PaginationReturn<ManagementEventListItem> | undefined,
            myCohostEventsData: allMyCohostEventsData as PaginationReturn<ManagementEventListItem> | undefined,
            allMyEvents: allMyEventsData as PaginationReturn<ManagementEventListItem> | undefined,
            allEvents: allEventsData as PaginationReturn<ManagementEventListItem> | undefined,
        }),
        [
            allEventsData,
            allMyCohostEventsData,
            allMyEventsData,
            allMyOrganizedEventsData,
            allMyUpcomingEventsCount,
            allUpcomingEventsCount,
        ]
    );

    const { data: countries } = useGetCountriesQuery();

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setTitle('Dashboard - Event List'));
    }, [dispatch]);

    const handleParamChange = useCallback(
        ({
            field,
            value,
            isDelete,
            pageFields = ['page'],
        }: {
            field: string;
            value: string | string[] | null;
            isDelete?: boolean;
            pageFields?: string[];
        }) => {
            if (field === 'tab') {
                setSearchParams({ tab: value ?? '', page: '1' });
                setDateError(null);
            } else if (isDelete) {
                const values = searchParams.get(field);

                if (values?.split(',').length === 1) {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        pageFields.forEach((field) => params.set(field, '1'));
                        params.delete(field);
                        return params;
                    });
                } else {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        pageFields.forEach((field) => params.set(field, '1'));
                        params.set(
                            field,
                            values
                                ?.split(',')
                                .filter((x) => x !== value)
                                .join(',') ?? ''
                        );
                        return params;
                    });
                }
            } else if (searchParams.get(field) !== value) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    pageFields.forEach((field) => params.set(field, '1'));
                    if (value) {
                        params.set(field, typeof value === 'string' ? value : value.join(','));
                    } else {
                        params.delete(field);
                    }
                    return params;
                });
            }
        },
        [searchParams, setSearchParams, setDateError]
    );

    const { data: organizers, isLoading: isOrganizersLoading } = useGetEventOrganizersQuery(undefined, {
        selectFromResult: (response) => ({
            ...response,
            data: [
                { label: 'Me', value: user?.id.toString() ?? '' },
                ...(response.data?.map((organizer) => ({
                    label:
                        organizer.first_name && organizer.last_name
                            ? `${organizer.first_name} ${organizer.last_name}`
                            : organizer.full_name,
                    value: organizer.id.toString(),
                })) ?? []),
            ],
        }),
    });

    // For All Events table dates
    const onAllDatesChange = useCallback(
        (start?: Date | undefined, end?: Date | undefined, myEventsTable?: boolean) => {
            const [minDate, maxDate] =
                timeframe === Timeframes.UPCOMING
                    ? [startOfToday(), undefined]
                    : timeframe === Timeframes.PAST
                    ? [undefined, startOfToday()]
                    : [undefined, undefined];

            if (validateRange([start ?? null, end ?? null], setDateError, minDate, maxDate)) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('page', '1');
                    if (!start || !end) {
                        setDateError(null);
                    }

                    params.set(`${myEventsTable ? 'my_' : ''}date_range_start`, getDateRangeParamValue(start));
                    params.set(`${myEventsTable ? 'my_' : ''}date_range_end`, getDateRangeParamValue(end));

                    return params;
                });
            }
        },
        [setDateError, setSearchParams, timeframe]
    );

    const handleAllDateClear = (myEventsTable?: boolean) => {
        onAllDatesChange(undefined, undefined, myEventsTable);
    };

    const handleSelectClick = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value, name },
        } = event;
        handleParamChange({ field: name, value: Array.isArray(value) ? value.join(',') : value });
    };

    const renderRow = useCallback(
        (event: Partial<ManagementEventListItem & Pick<IEvent, 'number_attendees'>>) => (
            <>
                <TableCell className="max-w-48">
                    <div className="flex gap-2 items-center">
                        {event.country_code ? (
                            <Flag flag={event.country_code.toLowerCase()} className="w-[27px] rounded h-[18px]" />
                        ) : [EventTypes.PRIVATE_VIRTUAL, EventTypes.PUBLIC_VIRTUAL].includes(
                              (event.type as string)?.replace('_', '-') as EventTypes
                          ) ? (
                            <img className="w-[27px] h-[18px] rounded" src={AbstractGlobe} />
                        ) : (
                            <div className="w-[27px]" />
                        )}
                        {event.name}
                    </div>
                </TableCell>
                <TableCell>{getEventTypeName(event.type) ?? ''}</TableCell>
                <TableCell>
                    {event.date_start && event.date_end
                        ? `${format(event.date_start, 'dd MMM')} - ${format(event.date_end, 'dd MMM yyyy')}`
                        : null}
                </TableCell>
                <TableCell>
                    {event.number_attendees}/{event.max_capacity}
                </TableCell>
                <TableCell component="th" scope="row">
                    <Chip
                        label={event.published ? 'YES' : 'NO'}
                        color={event.published ? 'light-success' : 'light-error'}
                        className="font-semibold"
                    />
                </TableCell>
                <TableCell>
                    {event?.level_badge_requirements && Number(event?.level_badge_requirements?.length) > 0 ? (
                        <BadgeDisplay badges={event.level_badge_requirements} />
                    ) : null}
                </TableCell>
            </>
        ),
        []
    );

    const renderKey = useCallback((event: Partial<ManagementEventListItem>) => `event-${event.type}-${event.id}`, []);

    const handleSortClick = useCallback(
        (column: keyof ManagementEventListItem) => {
            const isDesc = sort.column === column && sort.direction === 'desc';
            handleParamChange({ field: 'sort', value: `${!isDesc ? '-' : ''}${column}` });
        },
        [sort.column, sort.direction, handleParamChange]
    );
    const handleMySortClick = useCallback(
        (column: keyof ManagementEventListItem) => {
            const isDesc = mySort.column === column && mySort.direction === 'desc';
            handleParamChange({ field: 'mySort', value: `${!isDesc ? '-' : ''}${column}`, pageFields: ['myPage'] });
        },
        [mySort.column, mySort.direction, handleParamChange]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('page', `${newPage}`);
                return params;
            });
        },
        [setSearchParams]
    );
    const handleMyPageChange = useCallback(
        (newPage: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('myPage', `${newPage}`);
                return params;
            });
        },
        [setSearchParams]
    );

    const handleRowsPerPageChange = useCallback(
        (newRows: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('rows', `${newRows}`);
                return params;
            });
        },
        [setSearchParams]
    );

    const handleMyRowsPerPageChange = useCallback(
        (newRows: number) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.set('myRows', `${newRows}`);
                return params;
            });
        },
        [setSearchParams]
    );

    const handleSearchParamChange = useCallback(
        (value: string | null) => handleParamChange({ field: 'search', value }),
        [handleParamChange]
    );
    const handleMySearchParamChange = useCallback(
        (value: string | null) => handleParamChange({ field: 'mySearch', value, pageFields: ['myPage'] }),
        [handleParamChange]
    );
    const handleTabChange = useCallback(
        (_event: React.SyntheticEvent, value: string) => {
            handleParamChange({ field: 'tab', value: `${value}` });
            handleParamChange({ field: 'timeframe', value: value });
        },
        [handleParamChange]
    );
    useDebounce({
        value: search,
        delay: 500,
        customHandler: handleSearchParamChange,
        trigger: () => (searchParams.get('search') ?? '') !== search,
    });
    useEffect(() => {
        setSearch(filters.search as string);
    }, [filters.search, setSearchParams]);

    useDebounce({
        value: mySearch,
        delay: 500,
        customHandler: handleMySearchParamChange,
        trigger: () => (searchParams.get('mySearch') ?? '') !== mySearch,
    });
    useEffect(() => {
        setMySearch(filters.mySearch as string);
    }, [filters.mySearch, setSearchParams]);

    const resetFilters = useCallback(
        (isMyTable: boolean) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                if (isMyTable) {
                    params.set('myPage', '1');
                    Object.values(MyTableFilters).forEach((filter) => params.delete(filter));
                } else {
                    params.set('page', '1');
                    Object.values(AllTableFilters).forEach((filter) => params.delete(filter));
                }
                return params;
            });
        },
        [setSearchParams]
    );

    const resetMyFilters = useCallback(() => resetFilters(true), [resetFilters]);
    const resetAllFilters = useCallback(() => resetFilters(false), [resetFilters]);

    const clearSearch = () => {
        setSearch('');
        handlePageChange(1);
    };
    const clearMySearch = () => {
        setMySearch('');
        handleMyPageChange(1);
    };

    const renderCountryOption = useCallback(
        (item: Option) => (
            <ListItemText
                className="pt-1"
                slotProps={{
                    primary: {
                        className: '!flex items-center gap-2 !text-sm',
                    },
                }}
            >
                {item.value === 'webinar' ? (
                    <img className="w-[27px] h-[15px] rounded-sm" src={AbstractGlobe} />
                ) : (
                    <Flag className="w-[27px] h-[15px] rounded-sm" flag={(item.value as string).toLowerCase()} />
                )}
                <p>{item.label}</p>
            </ListItemText>
        ),
        []
    );

    const { data: badgesAndLevels, isLoading: isBadgesAndLevelsLoading } = useGetBadgesAndLevelsOptionsQuery();

    const renderLanguageOption = useCallback(
        ({ label, value }: Option) => (
            <ListItemText
                slotProps={{
                    primary: {
                        className: 'flex items-center gap-2',
                    },
                }}
            >
                <Flag flag={value == '' ? 'victron' : (value as string)} />
                <Typography>{label}</Typography>
            </ListItemText>
        ),
        []
    );

    const getFilterLabel = (filter: string, value: string) => {
        const eventTypes = EVENT_TYPES.map((type) => ({ ...type, value: type.value.replace('-', '_') }));
        const filters = EVENT_STATUS.concat(
            EVENT_ORGANIZER,
            EVENT_TIMEFRAME,
            eventTypes,
            YES_NO,
            ATTENDEE_PRESENT,
            ATTENDEE_STATUS
        );

        // Clean filter name by removing 'my_' prefix if present
        const cleanFilter = filter.replace('my_', '');

        if (['date_range_start', 'date_range_end'].includes(cleanFilter)) {
            const label = EVENT_DATE_FILTERS.find((x) => x.value === cleanFilter)?.label ?? cleanFilter;
            const date = getValidDateFromParam(value);
            return `${label} ${date ? format(date, 'yyyy-MM-dd') : value}`;
        } else if (cleanFilter === 'owner_id') {
            return `Organizer: ${
                value === user?.id.toString() ? 'Me' : organizers?.find((organizer) => organizer.value === value)?.label
            }`;
        } else if (cleanFilter === 'country_code' && value === 'webinar') {
            return 'Webinar';
        } else if (cleanFilter === 'privacy') {
            return capitalizeFirstLetter(value);
        } else if (cleanFilter === 'spoken_language') {
            return languageOptions?.find((lo) => lo.value == value)?.label ?? value;
        } else if (cleanFilter === 'level_id') {
            return `Badge Level: ${badgesAndLevels?.levels.find((l) => l.value == value)?.label ?? value}`;
        } else if (cleanFilter === 'badge_id') {
            return `Badge Solution: ${badgesAndLevels?.badges.find((b) => b.value == value)?.label ?? value}`;
        }
        return `${filters.find((filter) => filter.value === value)?.label ?? value}`;
    };

    const getMyFilterLabel = (filter: string, value: string) => getFilterLabel(filter, value);
    const getAllFilterLabel = (filter: string, value: string) => getFilterLabel(filter, value);

    useEffect(() => {
        if (
            !isMyOrganizedEventsFetching &&
            !isMyCohostEventsFetching &&
            Number(myOrganizedEventsData?.count) <= 0 &&
            Number(myCohostEventsData?.count) > 0
        ) {
            handleParamChange({ field: 'myTab', value: `${MY_TABS.COHOST}` });
        } else if (
            !isMyOrganizedEventsFetching &&
            !isMyCohostEventsFetching &&
            Number(myCohostEventsData?.count) <= 0 &&
            Number(myOrganizedEventsData?.count) > 0
        ) {
            handleParamChange({ field: 'myTab', value: `${MY_TABS.ORGANIZED}` });
        }

        // Don't include the handleParamChange as a dependency, otherwise the user can't swap tabs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMyOrganizedEventsFetching, isMyCohostEventsFetching, myOrganizedEventsData, myCohostEventsData]);

    return (
        <div className="pr-2.5">
            <div className="flex justify-between items-center bg-white rounded px-5 py-2 mb-5">
                <MUITabs
                    value={timeframe}
                    onChange={handleTabChange}
                    slotProps={{
                        indicator: {
                            sx: {
                                display: 'block',
                            },
                        },
                    }}
                >
                    <Tab
                        value={Timeframes.UPCOMING}
                        label={
                            <Typography color="black.main">
                                Upcoming Events
                                <Typography component="span" className="ml-2 opacity-40">
                                    {upcomingCount?.count} events
                                </Typography>
                            </Typography>
                        }
                    />
                    <Tab value={Timeframes.PAST} label={<Typography color="black.main">Past Events</Typography>} />
                    <Tab value={Timeframes.ALL} label={<Typography color="black.main">All Events</Typography>} />
                </MUITabs>

                <Button
                    className="h-[40px] min-w-[216px] text-nowrap rounded-full"
                    startIcon={<AddIcon />}
                    onClick={() => setIsModalOpen(true)}
                    disabled={isDistributorOrAmbassador}
                >
                    <Typography fontWeight={500} fontSize={15}>
                        CREATE NEW EVENT
                    </Typography>
                </Button>
            </div>
            <Accordion
                className="p-4 rounded mb-5"
                onChange={(_, expanded) =>
                    setExpanded((prev) => ({
                        ...prev,
                        my: expanded,
                    }))
                }
                expanded={expanded.my}
            >
                <PlusAccordionSummary isExpanded={expanded.my} className="px-5">
                    <Typography fontFamily="museo-sans, sans-serif" variant="h5">
                        My{' '}
                        {timeframe === Timeframes.UPCOMING ? 'Upcoming' : timeframe === Timeframes.PAST ? 'Past' : ''}{' '}
                        Events
                    </Typography>
                </PlusAccordionSummary>
                <AccordionDetails className="p-0">
                    <Typography className="mx-5 mb-5">
                        Browse the events you've organized and {timeframe === Timeframes.UPCOMING ? 'currently' : ''}{' '}
                        co-host
                        {timeframe === Timeframes.UPCOMING ? '' : 'ed'}.
                    </Typography>
                    <Filters
                        searchInput={mySearch ?? ''}
                        onSearchChange={(e) => setMySearch(e.target.value)}
                        onSearchClear={clearMySearch}
                        withFilterIcon={false}
                        searchPlaceholder="Search Event"
                        dividers={false}
                        getChipLabel={getMyFilterLabel}
                        onChipClick={(field, value) => handleParamChange({ field, value, isDelete: true })}
                        onClearFilterClick={resetMyFilters}
                        emblaCarouselOptions={{
                            dragFree: true,
                        }}
                        searchSpan={1}
                        activeFilters={myTableFilters}
                        activeFiltersPlacement="bottom"
                    >
                        <div className="my-3 grid grid-cols-4 gap-x-2.5 gap-y-5">
                            <Select
                                multiple
                                name="my_privacy"
                                size="small"
                                label="Privacy"
                                items={PRIVACY_FILTER_OPTIONS}
                                fcSX={{ size: 'small' }}
                                value={myTableFilters['my_privacy'] ?? []}
                                onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                className="rounded-lg"
                            />
                            <DateRangePicker
                                singleInput
                                shortcuts={
                                    timeframe === Timeframes.UPCOMING
                                        ? UPCOMING_DATE_PICKER_SHORTCUTS
                                        : timeframe === Timeframes.PAST
                                        ? PAST_DATE_PICKER_SHORTCUTS
                                        : DATE_PICKER_SHORTCUTS
                                }
                                handleDateClear={() => handleAllDateClear(true)}
                                disablePast={timeframe === Timeframes.UPCOMING}
                                disableFuture={timeframe === Timeframes.PAST}
                                calendars={1}
                                value={[
                                    getValidDateFromParam(myTableFilters.my_date_range_start?.[0]),
                                    getValidDateFromParam(myTableFilters.my_date_range_end?.[0]),
                                ]}
                                onChange={(value) => {
                                    const [start, end] = value;
                                    onAllDatesChange(start ?? undefined, end ?? undefined, true);
                                }}
                                slotProps={{
                                    textField: {
                                        error: !!dateError,
                                        helperText: dateError,
                                    },
                                    layout: {
                                        alertMessage:
                                            timeframe === Timeframes.UPCOMING
                                                ? UPCOMING_ALERT_MESSAGE
                                                : timeframe === Timeframes.PAST
                                                ? PAST_ALERT_MESSAGE
                                                : undefined,
                                    },
                                }}
                            />
                            <Select
                                multiple
                                name="my_published"
                                size="small"
                                label="Status"
                                items={EVENT_STATUS}
                                fcSX={{ size: 'small' }}
                                value={myTableFilters.my_published ?? []}
                                onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                className="rounded-lg"
                            />
                            <Select
                                searchable
                                searchProps={{
                                    autoFocus: false,
                                }}
                                multiple
                                name="my_level_id"
                                size="small"
                                loading={isBadgesAndLevelsLoading}
                                label="Badge Level"
                                items={badgesAndLevels?.levels ?? []}
                                fcSX={{ size: 'small' }}
                                renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                value={myTableFilters.my_level_id ?? []}
                                onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                virtualize
                                className="rounded-lg"
                            />
                            <Select
                                searchable
                                searchProps={{
                                    autoFocus: false,
                                }}
                                multiple
                                name="my_badge_id"
                                size="small"
                                loading={isBadgesAndLevelsLoading}
                                label="Badge Solution"
                                items={badgesAndLevels?.badges ?? []}
                                fcSX={{ size: 'small' }}
                                renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                value={myTableFilters.my_badge_id ?? []}
                                onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                virtualize
                                className="rounded-lg"
                            />
                        </div>
                    </Filters>
                    <div className="px-5">
                        <Tabs
                            value={myTab}
                            onChange={(_e, newValue) => handleParamChange({ field: 'myTab', value: `${newValue}` })}
                        >
                            <Tabs.Tab
                                label={
                                    <Typography fontWeight={500} fontSize={14}>
                                        Organized{'   '}
                                        <Typography
                                            component="span"
                                            fontSize={14}
                                            fontWeight={500}
                                            color="black40.main"
                                        >
                                            {myOrganizedEventsData?.count} Events
                                        </Typography>
                                    </Typography>
                                }
                            />
                            <Tabs.Tab
                                label={
                                    <Typography fontWeight={500} fontSize={14}>
                                        Co-host{'   '}
                                        <Typography
                                            component="span"
                                            fontSize={14}
                                            fontWeight={500}
                                            color="black40.main"
                                        >
                                            {myCohostEventsData?.count} Events
                                        </Typography>
                                    </Typography>
                                }
                            />
                        </Tabs>
                        <Table
                            className="min-w-[1100px]"
                            sort={mySort}
                            headers={EVENT_TABLE_HEADERS}
                            data={allMyEvents?.results ?? []}
                            renderRow={renderRow}
                            renderKey={renderKey}
                            getHref={(row) => `/app/dashboard/events/${row?.type}/${row.id}`}
                            hrefProps={{
                                target: '_blank',
                            }}
                            emptyListTxt="There are currently no events to list"
                            onSortClick={handleMySortClick}
                            page={Number(filters.myPage)}
                            onPageChange={handleMyPageChange}
                            count={allMyEvents?.count ?? 0}
                            isLoading={isMyEventsFetching}
                            rowsPerPage={Number(filters.myRows)}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            setRowsPerPage={handleMyRowsPerPageChange}
                            fillMissingRows={false}
                        />
                    </div>
                </AccordionDetails>
            </Accordion>
            {!isDistributorOrAmbassador ? (
                <Accordion
                    className="p-4 rounded"
                    onChange={(_, expanded) =>
                        setExpanded((prev) => ({
                            ...prev,
                            all: expanded,
                        }))
                    }
                    expanded={expanded.all}
                >
                    <PlusAccordionSummary isExpanded={expanded.all} className="px-5">
                        <Typography fontFamily="museo-sans, sans-serif" variant="h5">
                            All{' '}
                            {timeframe === Timeframes.UPCOMING
                                ? 'Upcoming'
                                : timeframe === Timeframes.PAST
                                ? 'Past'
                                : ''}{' '}
                            Events
                        </Typography>
                    </PlusAccordionSummary>
                    <AccordionDetails className="p-0">
                        <Typography className="mx-5 mb-5">
                            Browse the full list of{' '}
                            {timeframe === Timeframes.UPCOMING
                                ? 'all scheduled upcoming'
                                : timeframe === Timeframes.PAST
                                ? 'all past'
                                : ''}{' '}
                            events.
                        </Typography>
                        <Filters
                            searchInput={search ?? ''}
                            onSearchChange={(e) => setSearch(e.target.value)}
                            onSearchClear={clearSearch}
                            withFilterIcon={false}
                            searchPlaceholder="Search Event"
                            isOpen={isFilterOpen}
                            onFilterOpenToggle={() => setIsFilterOpen(!isFilterOpen)}
                            onClearFilterClick={resetAllFilters}
                            activeFilters={allTableFilters}
                            onChipClick={(field, value) => handleParamChange({ field, value, isDelete: true })}
                            getChipLabel={getAllFilterLabel}
                            emblaCarouselOptions={{
                                dragFree: true,
                            }}
                            activeFiltersPlacement="bottom"
                            dividers={false}
                            searchSpan={1}
                        >
                            <div className="my-3 grid grid-cols-4 gap-x-2.5 gap-y-5">
                                <Select
                                    multiple
                                    name="privacy"
                                    size="small"
                                    label="Privacy"
                                    items={PRIVACY_FILTER_OPTIONS}
                                    fcSX={{ size: 'small' }}
                                    value={allTableFilters['privacy'] ?? []}
                                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                    className="rounded-lg"
                                />
                                <DateRangePicker
                                    singleInput
                                    shortcuts={
                                        timeframe === Timeframes.UPCOMING
                                            ? UPCOMING_DATE_PICKER_SHORTCUTS
                                            : timeframe === Timeframes.PAST
                                            ? PAST_DATE_PICKER_SHORTCUTS
                                            : DATE_PICKER_SHORTCUTS
                                    }
                                    handleDateClear={handleAllDateClear}
                                    disablePast={timeframe === Timeframes.UPCOMING}
                                    disableFuture={timeframe === Timeframes.PAST}
                                    calendars={1}
                                    value={[
                                        getValidDateFromParam(allTableFilters.date_range_start?.[0]),
                                        getValidDateFromParam(allTableFilters.date_range_end?.[0]),
                                    ]}
                                    onChange={(value) => {
                                        const [start, end] = value;
                                        onAllDatesChange(start ?? undefined, end ?? undefined);
                                    }}
                                    slotProps={{
                                        textField: {
                                            error: !!dateError,
                                            helperText: dateError,
                                        },
                                        layout: {
                                            alertMessage:
                                                timeframe === Timeframes.UPCOMING
                                                    ? UPCOMING_ALERT_MESSAGE
                                                    : timeframe === Timeframes.PAST
                                                    ? PAST_ALERT_MESSAGE
                                                    : undefined,
                                        },
                                    }}
                                />
                                <Select
                                    multiple
                                    name="published"
                                    size="small"
                                    label="Status"
                                    items={EVENT_STATUS}
                                    fcSX={{ size: 'small' }}
                                    value={allTableFilters.published ?? []}
                                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                    className="rounded-lg"
                                />
                                <Select
                                    searchable
                                    searchProps={{
                                        autoFocus: false,
                                    }}
                                    multiple
                                    name="level_id"
                                    size="small"
                                    loading={isBadgesAndLevelsLoading}
                                    label="Badge Level"
                                    items={badgesAndLevels?.levels ?? []}
                                    fcSX={{ size: 'small' }}
                                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                    value={allTableFilters.level_id ?? []}
                                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                    virtualize
                                    className="rounded-lg"
                                />
                                <Select
                                    searchable
                                    searchProps={{
                                        autoFocus: false,
                                    }}
                                    multiple
                                    name="badge_id"
                                    size="small"
                                    loading={isBadgesAndLevelsLoading}
                                    label="Badge Solution"
                                    items={badgesAndLevels?.badges ?? []}
                                    fcSX={{ size: 'small' }}
                                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                    value={allTableFilters.badge_id ?? []}
                                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                    virtualize
                                    className="rounded-lg"
                                />
                                <Select
                                    multiple
                                    searchable
                                    virtualize
                                    name="owner_id"
                                    size="small"
                                    label="Organizer"
                                    items={organizers}
                                    loading={isOrganizersLoading}
                                    fcSX={{ size: 'small' }}
                                    value={allTableFilters.owner_id ?? []}
                                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                    className="rounded-lg"
                                />
                                <Select
                                    searchable
                                    multiple
                                    name="country_code"
                                    size="small"
                                    label="Country"
                                    items={
                                        countries ? [{ label: 'Virtual Event', value: 'webinar' }, ...countries] : []
                                    }
                                    fcSX={{ size: 'small' }}
                                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                    value={allTableFilters.country_code ?? []}
                                    renderOption={renderCountryOption}
                                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                    virtualize
                                    className="rounded-lg"
                                />
                                <Select
                                    searchable
                                    multiple
                                    name="spoken_language"
                                    size="small"
                                    label="Language"
                                    items={languageOptions ?? []}
                                    loading={areLanguageOptionsLoading}
                                    fcSX={{ size: 'small' }}
                                    renderValue={(selected) => <div>{(selected as [])?.length} selected</div>}
                                    value={allTableFilters.spoken_language ?? []}
                                    renderOption={renderLanguageOption}
                                    onChange={(e) => handleSelectClick(e as SelectChangeEvent<string[]>)}
                                    virtualize
                                    className="rounded-lg"
                                />
                            </div>
                        </Filters>
                        <div className="bg-white rounded px-5 py-2">
                            <Table
                                className="min-w-[1100px]"
                                sort={sort}
                                headers={EVENT_TABLE_HEADERS}
                                data={allEvents?.results ?? []}
                                renderRow={renderRow}
                                renderKey={renderKey}
                                getHref={(row) => `/app/dashboard/events/${row?.type}/${row.id}`}
                                hrefProps={{
                                    target: '_blank',
                                }}
                                emptyListTxt="There are currently no events to list"
                                onSortClick={handleSortClick}
                                page={Number(filters.page)}
                                onPageChange={handlePageChange}
                                count={allEvents?.count ?? 0}
                                isLoading={isAllEventsFetching}
                                rowsPerPage={Number(filters.rows)}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                setRowsPerPage={handleRowsPerPageChange}
                                fillMissingRows={false}
                            />
                        </div>
                    </AccordionDetails>
                </Accordion>
            ) : null}
            <EventCreationDialog open={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </div>
    );
};

export { EventList };
